import {defineRouting} from 'next-intl/routing'

import {PAGES_ROUTER_FOLDERS} from '../../router-manifest'
import {Locale} from '../constants'
import {getPathLocales} from './restrictedPaths'

export const routing = defineRouting({
  locales: Object.values(Locale),
  defaultLocale: Locale.en,
  localePrefix: 'as-needed',
  // The URL alone decides the language (docs/visitor-preferences.md)
  localeDetection: false,
  // NEXT_LOCALE records explicit choices only, so next-intl must not write it on every visit
  localeCookie: false,
  // The middleware would otherwise advertise all of `locales` as hreflang alternates in a `Link`
  // header on every response, which is wrong for all but the widest content tier: the base customer
  // journey exists in `fullySupportedLocales` only, and some marketing pages are en or en+es. Each
  // page already declares its own set via `generateDefaultAlternates`, matching what `app/sitemap.ts`
  // emits, so the header can only contradict them - and a contradictory hreflang cluster is one
  // Google discards wholesale, taking the correct locales down with the wrong ones.
  alternateLinks: false,
})

export const isPagesRouterPath = (path: string) => {
  const segments = path.split('/').filter(Boolean)
  const topLevelFolder = routing.locales.includes(segments[0] as any) ? segments[1] : segments[0]
  return PAGES_ROUTER_FOLDERS.includes(topLevelFolder)
}

/**
 * The only routes that may follow the visitor's saved language rather than their URL: their
 * responses are never cached - the portal is behind the login, and the homepage still renders per
 * request. A public page cannot, because the edge serves one copy of it to everyone. When the
 * homepage becomes static, it has to move to a pre-paint script instead.
 */
export const isPreferenceRoutedPath = (cleanedPath: string) =>
  cleanedPath === '/' || cleanedPath === '/portal' || cleanedPath.startsWith('/portal/')

// The saved language when this path may follow it and is served in it
export const getPreferredPathLocale = (cleanedPath: string, preferred: string | undefined) =>
  isPreferenceRoutedPath(cleanedPath) && getPathLocales(cleanedPath).includes(preferred as Locale)
    ? (preferred as Locale)
    : undefined
